'use client'

import React, { createContext, useContext, useState, useEffect } from 'react'
import { checkSession } from '@/modules/auth/auth.api'
import type { AuthState, CheckSessionResponse } from '@/modules/auth/types/auth.types'

interface AuthContextType extends AuthState {}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: React.ReactNode
  /**
   * Initial auth state from server (optional)
   * If provided, skips initial client-side auth check
   */
  initialAuth?: CheckSessionResponse
}

export function AuthProvider({ children, initialAuth }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>(() => {
    // If initial auth is provided from server, use it
    if (initialAuth) {
      return {
        isAuthenticated: initialAuth.authenticated,
        user: initialAuth.user || null,
        isLoading: false,
      }
    }

    // Otherwise, start with loading state
    return {
      isAuthenticated: false,
      user: null,
      isLoading: true,
    }
  })

  useEffect(() => {
    import('posthog-js').then(({ default: posthog }) => {
      if (authState.user && typeof window !== 'undefined') {
        posthog.identify(String(authState.user.id), {
          email: authState.user.email,
          name: authState.user.display_name,
          username: authState.user.username,
        })
      } else if (!authState.isLoading && !authState.isAuthenticated) {
        posthog.reset()
      }
    })
  }, [authState.user, authState.isAuthenticated, authState.isLoading])

  return (
    <AuthContext.Provider value={{ ...authState }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
